import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControl, FormControlLabel, Grid, InputLabel, LinearProgress, MenuItem,
  Paper, Select, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
  useTheme,
} from '@mui/material';
import { getStudyProgress, getStudySettings, updateStudySettings } from '../api';
import { useLang } from '../i18n';

const pctText = (v) => (v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`);

function StatTile({ label, value, hint }) {
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Paper>
  );
}

// Столбики «минуты в день» за 30 дней: один ряд, цвет темы, пунктир — дневная цель.
function MinutesChart({ daily, goal }) {
  const theme = useTheme();
  const { lang, t } = useLang();
  const locale = lang === 'it' ? 'it-IT' : 'ru-RU';
  const height = 120;
  const max = Math.max(goal, ...daily.map((d) => d.minutes), 1);
  const goalY = height - (goal / max) * height;
  return (
    <Box>
      <Box
        role="img"
        aria-label={t('progress.chartLabel', { goal })}
        sx={{ position: 'relative', height, display: 'flex', alignItems: 'flex-end', gap: '2px' }}
      >
        <Box sx={{
          position: 'absolute', left: 0, right: 0, top: goalY, borderTop: `1px dashed ${theme.palette.text.disabled}`,
        }} />
        <Typography variant="caption" color="text.secondary"
          sx={{ position: 'absolute', right: 0, top: Math.max(0, goalY - 16), lineHeight: 1 }}>
          {t('progress.goalLine', { goal })}
        </Typography>
        {daily.map((d) => (
          <Tooltip key={d.date} arrow
            title={t('progress.tooltip', {
              d: new Date(d.date).toLocaleDateString(locale, { day: 'numeric', month: 'short' }),
              m: Math.round(d.minutes), n: d.answers,
            }) + (d.answers ? t('progress.tooltipCorrect', { p: Math.round((d.correct / d.answers) * 100) }) : '')}>
            <Box sx={{ flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end', cursor: 'default' }}>
              <Box sx={{
                width: '100%',
                height: d.minutes > 0 ? `${Math.max(2, (d.minutes / max) * 100)}%` : '1px',
                bgcolor: d.minutes > 0 ? theme.palette.primary.main : theme.palette.divider,
                borderRadius: '4px 4px 0 0',
              }} />
            </Box>
          </Tooltip>
        ))}
      </Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">{t('progress.daysAgo')}</Typography>
        <Typography variant="caption" color="text.secondary">{t('progress.todayAxis')}</Typography>
      </Stack>
    </Box>
  );
}

function SettingsPanel({ onSaved }) {
  const { t, setLang } = useLang();
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { getStudySettings().then(setS).catch(() => {}); }, []);
  if (!s) return null;

  const lessonCourse = s.courses.find((c) => c === 'petrov' || c === 'russo') || '';
  const personal = s.courses.includes('personal');
  const setCourses = (nextLessonCourse, nextPersonal) => setS({
    ...s, courses: [nextLessonCourse, nextPersonal ? 'personal' : null].filter(Boolean),
  });

  const save = async () => {
    const courseChanged = lessonCourse !== (s.lesson_course || '');
    const res = await updateStudySettings({
      goal_minutes: Number(s.goal_minutes), new_per_day: Number(s.new_per_day), courses: s.courses,
      // при смене курса сервер сам выставит первый урок
      ...(courseChanged ? {} : { petrov_lesson: Number(s.petrov_lesson) }),
      leaderboard_hidden: s.leaderboard_hidden, ui_lang: s.ui_lang,
    });
    setS(res);
    setLang(res.ui_lang);
    setSaved(true);
    onSaved();
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>{t('progress.settings')}</Typography>
      <Stack spacing={2}>
        <TextField label={t('progress.goal')} type="number" value={s.goal_minutes}
          onChange={(e) => setS({ ...s, goal_minutes: e.target.value })} inputProps={{ min: 5, max: 120 }} />
        <TextField label={t('progress.newPerDay')} type="number" value={s.new_per_day}
          onChange={(e) => setS({ ...s, new_per_day: e.target.value })} inputProps={{ min: 0, max: 50 }} />
        <FormControl>
          <InputLabel>{t('progress.whatToStudy')}</InputLabel>
          <Select label={t('progress.whatToStudy')} value={lessonCourse}
            onChange={(e) => setCourses(e.target.value, personal)}>
            <MenuItem value="petrov">{t('progress.course.petrov')}</MenuItem>
            <MenuItem value="russo">{t('progress.course.russo')}</MenuItem>
            <MenuItem value="">—</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={personal} onChange={(e) => setCourses(lessonCourse, e.target.checked)} />}
          label={t('progress.course.personal')}
        />
        <TextField label={t('progress.openUpTo')} type="number" value={s.petrov_lesson}
          helperText={t('progress.openUpToHint')}
          onChange={(e) => setS({ ...s, petrov_lesson: e.target.value })} inputProps={{ min: 0, max: 20 }} />
        <FormControl>
          <InputLabel>{t('progress.uiLang')}</InputLabel>
          <Select label={t('progress.uiLang')} value={s.ui_lang}
            onChange={(e) => setS({ ...s, ui_lang: e.target.value })}>
            <MenuItem value="ru">Русский</MenuItem>
            <MenuItem value="it">Italiano</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={!s.leaderboard_hidden}
            onChange={(e) => setS({ ...s, leaderboard_hidden: !e.target.checked })} />}
          label={t('progress.showOnBoard')}
        />
        <Button variant="contained" onClick={save}>{t('progress.save')}</Button>
        {saved && <Typography variant="caption" color="text.secondary">{t('progress.saved')}</Typography>}
      </Stack>
    </Paper>
  );
}

export default function Progress() {
  const { lang, t } = useLang();
  const locale = lang === 'it' ? 'it-IT' : 'ru-RU';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(
    () => getStudyProgress().then(setData).catch(() => setError(t('progress.error'))), [t],
  );
  useEffect(() => { load(); }, [load]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <CircularProgress />;

  const totals = data.totals;
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: 2 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">{t('progress.title')}</Typography>
        <Button component={Link} to="/study" variant="contained">{t('progress.studyNow')}</Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile label={t('progress.streak')} value={`🔥 ${data.streak}`} hint={t('progress.streakHint')} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile label={t('progress.today')} value={`${Math.round(data.minutes_today)} / ${t('board.min', { m: data.goal_minutes })}`} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile label={t('progress.known')} value={totals.known}
            hint={t('progress.knownHint', { l: totals.learning, d: totals.due_now })} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile label={t('progress.accuracy')} value={pctText(data.accuracy_7d)}
            hint={t('progress.accuracy30', { p: pctText(data.accuracy_30d) })} />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>{t('progress.minutesPerDay')}</Typography>
        <MinutesChart daily={data.daily} goal={data.goal_minutes} />
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>{t('progress.level')}</Typography>
        {data.assessments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">{t('progress.levelEmpty')}</Typography>
        ) : (
          <Stack spacing={1}>
            {[...data.assessments].reverse().map((a) => (
              <Box key={a.date}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Chip label={a.cefr || '—'} color="primary" size="small" />
                  <Typography variant="body2">{new Date(`${a.date}Z`).toLocaleDateString(locale)}</Typography>
                  <Typography variant="caption" color="text.secondary">{a.source}</Typography>
                </Stack>
                {a.summary && <Typography variant="body2" sx={{ mt: 0.5 }}>{a.summary}</Typography>}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {data.tenses.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>{t('progress.tenses')}</Typography>
          <Table size="small" sx={{ '& td, & th': { px: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('progress.tense')}</TableCell>
                <TableCell align="right">{t('progress.correct')}</TableCell>
                <TableCell align="right">{t('progress.solid')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.tenses.map((row) => (
                <TableRow key={row.tense}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell align="right">{pctText(row.accuracy)}</TableCell>
                  <TableCell align="right">{row.known}/{row.verbs}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {data.lessons.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>{t('progress.lessons')}</Typography>
          <Stack spacing={1.5}>
            {data.lessons.map((l) => (
              <Box key={l.n} sx={{ opacity: l.unlocked ? 1 : 0.5 }}>
                <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                  <Typography variant="body2">
                    {l.n}. {l.title}{' '}
                    <Typography component="span" variant="caption" color="text.secondary">{l.level}</Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('progress.lessonStats', { i: l.introduced, t: l.total, k: l.known })}
                  </Typography>
                </Stack>
                <LinearProgress variant="determinate" value={l.total ? (l.introduced / l.total) * 100 : 0}
                  sx={{ height: 6, borderRadius: 3 }} />
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      <SettingsPanel onSaved={load} />
    </Box>
  );
}
